/**
 * OrderEditModal — staff edit of a single attendee's pre-order (Responses view).
 *
 * Reuses the public form's ItemPicker + FieldRenderer so admin edits match what the
 * customer sees. Saves via `placeOrder` (isManager path) which preserves the order's
 * deviceId, keeps the phone→order claim consistent, and appends an audit entry.
 */
import React, { useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Modal from './Modal';
import FieldRenderer from './FieldRenderer';
import ItemPicker from './ItemPicker';
import { placeOrder } from '../eventsCloud';
import { buildOrderPayload, diffOrder, orderEditEntry, formatMoney, orderTotal } from '../eventUtils';
import { fieldIsInput } from '../fieldTypes';
import { t } from '../strings';

export default function OrderEditModal({ event, order, onClose, onSaved }) {
  const { user } = useAuth();
  const settings = event.settings || {};
  const items = event.menuItems || [];
  const fields = event.fields || [];

  const seedQ = useMemo(() => {
    const q = {};
    (order.items || []).forEach((it) => { if (it.id) q[it.id] = it.qty; });
    return q;
  }, [order]);

  const [name, setName] = useState(order.respondentName || '');
  const [phone, setPhone] = useState(order.respondentPhone || '');
  const [email, setEmail] = useState(order.respondentEmail || '');
  const [quantities, setQuantities] = useState(seedQ);
  const [answers, setAnswers] = useState(order.answers || {});
  const [orderText, setOrderText] = useState(order.orderText || '');
  const [instructions, setInstructions] = useState(order.instructions || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const subtotal = orderTotal(event, quantities);
  const setQty = (id, q) => setQuantities((p) => ({ ...p, [id]: q }));
  const setAnswer = (fid, v) => setAnswers((p) => ({ ...p, [fid]: v }));
  const inputFields = fields.filter((f) => fieldIsInput(f.type));

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = buildOrderPayload(
        { ...event, id: event.id },
        { respondentName: name, respondentPhone: phone, respondentEmail: email, quantities, answers, orderText, instructions },
        order.deviceId || '',
      );
      const prev = {
        quantities: seedQ, orderText: order.orderText || '', name: order.respondentName || '',
        phone: order.respondentPhone || '', email: order.respondentEmail || '', answers: order.answers || {},
      };
      const summary = diffOrder(event, prev, { quantities, orderText, name, phone, email, answers });
      const actor = user?.displayName || user?.email || 'Staff';
      await placeOrder(event.id, phone, payload, {
        knownOrderId: order.id,
        keepDeviceId: order.deviceId || null,
        editEntry: orderEditEntry(actor, summary || t('editedByAdmin')),
      });
      if (onSaved) onSaved();
      onClose();
    } catch {
      setError(t('orderSaveError'));
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`✎ ${t('editOrderTitle')}`} maxWidth={560}>
      <p className="ev-modal__sub">{t('editOrderSubtitle')}</p>
      {error ? <div className="evf__error">{error}</div> : null}
      <div className="ev-oe">
        <label className="ev-oe__label">{t('name')}</label>
        <input className="evf-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('name')} />

        <div className="ev-oe__grid2">
          <div>
            <label className="ev-oe__label">{t('phone')}</label>
            <input className="evf-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t('phone')} />
          </div>
          <div>
            <label className="ev-oe__label">{t('email')}</label>
            <input className="evf-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('email')} />
          </div>
        </div>

        {items.length > 0 ? (
          <>
            <label className="ev-oe__label">{t('orderItems')}</label>
            <ItemPicker items={items} quantities={quantities} onChange={setQty} showPrices={settings.showPrices} />
          </>
        ) : (
          <>
            <label className="ev-oe__label">{t('yourOrder')}</label>
            <textarea className="evf-input" rows={3} value={orderText} onChange={(e) => setOrderText(e.target.value)} />
          </>
        )}

        {inputFields.length > 0 && (
          <div className="ev-oe__fields">
            {inputFields.map((f) => (
              <FieldRenderer key={f.id} field={f} value={answers[f.id]} onChange={(v) => setAnswer(f.id, v)} />
            ))}
          </div>
        )}

        <label className="ev-oe__label">{t('specialInstructions')}</label>
        <textarea className="evf-input" rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder={t('instructionsPlaceholder')} />
      </div>

      <div className="ev-modal__actions">
        <button className="ev__btn ev__btn--ghost" onClick={onClose} disabled={busy}>{t('cancel')}</button>
        <button className="ev__btn" onClick={save} disabled={busy}>
          {busy ? t('saving') : `${t('saveChanges')}${settings.showPrices && subtotal > 0 ? ` · ${formatMoney(subtotal)}` : ''}`}
        </button>
      </div>
    </Modal>
  );
}
